import Countdown from 'react-countdown'
import { ShareIcon, RefreshIcon, InformationCircleIcon } from '@heroicons/react/outline'
import { StatBar } from '../stats/StatBar'
import { Histogram } from '../stats/Histogram'
import { GameStatus } from '../../components/games/Game'
import { Social } from '../../components/social/Social'
import { shareStatus, getShareText } from '../../lib/share'
import { WordList } from '../../lib/words'
import { BaseModal } from './BaseModal'
import { User } from '../../lib/user'
import { Events } from '../../components/events/Events'
import {
  STATISTICS_TITLE,
  GUESS_DISTRIBUTION_TEXT,
  NEW_WORD_TEXT,
  SHARE_TEXT,
  GAME_MODE_UNLIMITED,
  GAME_MODE_CUSTOM,
  GA_CATEGORY_NAV,
  GA_ACTION_SHARE,
} from '../../constants/strings'
import { MigrationIntro } from '../stats/MigrationIntro'
import { ENABLE_MIGRATE_STATS } from '../../constants/settings'
import { useAchievements } from '../../context/AchievementsContext'
import ReactGA from 'react-ga4';

type Props = {
  isOpen: boolean
  handleClose: () => void
  gameStatus: GameStatus|null
  shareUrl: string
  handleShareToClipboard: () => void
  handleShareFailure: () => void
  handleMigrateStatsButton: () => void
  isHardMode: boolean
  isExpertMode: boolean
  isDarkMode: boolean
  isHighContrastMode: boolean
  onNextPuzzle: () => void
  onStartUnlimited: () => void
  onJoinPPlus: () => void
  gameMode: string
  user: User
  list: WordList
}

export const StatsModal = ({
  isOpen,
  handleClose,
  gameStatus,
  shareUrl,
  handleShareToClipboard,
  handleShareFailure,
  handleMigrateStatsButton,
  isHardMode,
  isExpertMode,
  isDarkMode,
  isHighContrastMode,
  onNextPuzzle,
  onStartUnlimited,
  onJoinPPlus,
  gameMode,
  user,
  list
}: Props) => {
  const stats = user.stats()
  const tomorrow = list.getTomorrow() 
  const {grantKeyedAchievement} = useAchievements() 

  const handleShareSuccess = () => {
    grantKeyedAchievement(user, "share")
  }

  const share = (gs: GameStatus) => {
    ReactGA.event({
      category: GA_CATEGORY_NAV,
      action: GA_ACTION_SHARE,
    })
    shareStatus(
      gs.solution,
      gs.guesses,
      gs.isGameLost,
      isHardMode,
      isExpertMode,
      isDarkMode,
      isHighContrastMode,
      gameMode,
      handleShareToClipboard,
      handleShareSuccess,
      handleShareFailure,
      shareUrl,
      stats
    )
  }


  const shareText = (gs: GameStatus) => { 
    return getShareText(
                  gs.solution,
                  gs.guesses,
                  gs.isGameLost,
                  isHardMode,
                  isExpertMode,
                  isDarkMode,
                  isHighContrastMode,
                  gameMode,
                  shareUrl,
                  stats
                )
  }

  const joinPplus = () => {
    handleClose();
    onJoinPPlus();
  }


  const unlimitedButton = () => {
    const classNames = "inline-flex justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-4 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm row-span-1"
    if (gameMode === GAME_MODE_UNLIMITED) {
      return (<button
        id="stats-next-puzzle"
        type="button"
        className={classNames}
        onClick={onNextPuzzle}>
        <RefreshIcon className="h-6 w-6 mr-2 cursor-pointer dark:stroke-white scale-up" />
        Next puzzle
      </button>)
    }
    return (<button
      type="button"
      className={classNames}
      onClick={onStartUnlimited}>
      <span className="h-3 w-3 leading-1 mr-6 text-3xl text-white-500 cursor-pointer scale-up inf">∞</span>
      Play unlimited
    </button>)
  }

  const customStatsDisclaimer = () => {
    return <>{gameMode === GAME_MODE_CUSTOM && 
      <div className="flex items-center justify-center bg-indigo-500 text-white text-sm font-bold px-2 py-2 m-2 rounded" role="alert">
        <InformationCircleIcon className="h-6 w-6 mr-2 cursor-pointer dark:stroke-white scale-up" />
        <p className="text-gray-900 text-gray-100 p-2">Custom games do not count toward your overall stats</p>
    </div>}</>
  }

  const solvedStats = (gs: GameStatus|null) => {
    return (
        <>
        <Events />

        {gs && (gs.isGameWon || gs.isGameLost )&&
        <div className="grid grid-rows-2 grid-flow-col gap-2 dark:text-white mb-4">
          <div className="row-span-2 flex justify-center">
            <div className="self-center">
              <h5>{NEW_WORD_TEXT}</h5>
              <Countdown
                className="text-lg font-medium text-gray-900 dark:text-gray-100"
                date={tomorrow}
                daysInHours={true}
              />
            </div>
          </div>
          <button
              id="stats-share"
              type="button"
              className="inline-flex justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm row-span-1"
              data-share-text={shareText(gs)}
              onClick={() => share(gs)}>
              <ShareIcon className="h-6 w-6 mr-2 cursor-pointer dark:stroke-white scale-up" />
              {SHARE_TEXT}
            </button>
          {unlimitedButton()}
        </div>
        }
        <Social />
        </>
    )
  }

  if (stats.totalGames <= 0) {
    return (
      <BaseModal
        title={STATISTICS_TITLE}
        isOpen={isOpen}
        handleClose={handleClose}
      >
        {customStatsDisclaimer()}
        <StatBar gameStats={stats} />
        {ENABLE_MIGRATE_STATS && (
          <MigrationIntro handleMigrateStatsButton={handleMigrateStatsButton} />
        )}
      </BaseModal>
    )
  }
  return (
    <BaseModal
      title={STATISTICS_TITLE}
      isOpen={isOpen}
      handleClose={handleClose}
    >
      <a href="/achievements" className="text-sm title mt-4 text-indigo-700 dark:text-indigo-400 font-bold hover:dark:text-indigo-600 hover:text-indigo-800" >
        View your achievements
      </a>
      {customStatsDisclaimer()}
      <StatBar gameStats={stats} />
      <h4 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
        {GUESS_DISTRIBUTION_TEXT}
      </h4>
      <Histogram
        gameStats={stats}
        isGameWon={gameStatus?.isGameWon ?? false}
        numberOfGuessesMade={gameStatus ? gameStatus.guesses.length : 0}
        highlightCurrent={gameMode !== GAME_MODE_CUSTOM}
      />
      {solvedStats(gameStatus)}
      {!user.isAuthed() && ENABLE_MIGRATE_STATS && (
        <div>
          <hr className="mt-4 -mb-4 border-gray-500" />
          <MigrationIntro handleMigrateStatsButton={handleMigrateStatsButton} />
        </div>
      )}
    </BaseModal>
  )
}
